<template>
  <div>
    <breadcrumbs home="Item"/>

    <v-card flat>
      <v-tabs v-model="tab" class="full-height-tab" show-arrows>
        <v-tab>Info</v-tab>
        <v-tab v-if="itemId==undefined && item.type==='Item'">Initial</v-tab>
        <v-tab v-if="item.assembled">Assembly</v-tab>
        <v-tab>Purchasing</v-tab>
        <v-tab>Selling</v-tab>

        <v-tab-item>
          <inventory-items-edit-info :item="item" :accounts="accounts" ref="info" :companyAccounts="companyAccounts" :values="values" :disallowItem="disallowItem"/>
        </v-tab-item>
        <v-tab-item v-if="itemId==undefined && item.type==='Item'" eager>
          <inventory-items-edit-init :item="item" ref="init"/>
        </v-tab-item>
        <v-tab-item v-if="item.assembled" eager>
          <inventory-items-edit-assembly :item="item" ref="assembled"/>
        </v-tab-item>
        <v-tab-item eager>
          <inventory-items-edit-buying :item="item" :taxes="taxes.filter(tax => tax.purchaseRate !== null)" :accounts="accounts" :values="values" ref="buying"/>
        </v-tab-item>
        <v-tab-item eager>
          <inventory-items-edit-selling :item="item" :taxes="taxes.filter(tax => tax.salesRate !== null)" :accounts="accounts" :values="values" ref="selling"/>
        </v-tab-item>
      </v-tabs>
      <v-divider/>
      <v-card-actions class="pr-6">
        <v-spacer/>
        <v-btn plain color="primary" @click="cancel">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>
</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'

import InventoryItemsEditInfo from '@/views/inventory/items/InventoryItemsEditInfo'
import InventoryItemsEditBuying from '@/views/inventory/items/InventoryItemsEditBuying'
import InventoryItemsEditSelling from '@/views/inventory/items/InventoryItemsEditSelling'
import InventoryItemsEditInit from '@/views/inventory/items/InventoryItemsEditInit'
import InventoryItemsEditAssembly from '@/views/inventory/items/InventoryItemsEditAssembly'

export default {
  name: 'InventoryItemsEdit',
  mixins: [confirmLeave],
  components: {InventoryItemsEditInfo,InventoryItemsEditBuying,InventoryItemsEditSelling,InventoryItemsEditInit,InventoryItemsEditAssembly},
  props: {
    itemId: {type:String, required:false}
  },
  data () {
    return {
      accounts: [],
      companyAccounts: {},
      taxes: [],
      item: {vendors:[],customers:[],initial:[],productionPlanningInfo:{equipmentTypes:[]}},
      dirty: false,
      saving: false,
      loading: false,
      tab: 0,
      disallowItem: null
    }
  },
  computed: {
    connectedToAccounting() {
      return this.$store.getters.company.connectedToAccounting;
    },
    inventoryAccounts() {
      return this.accounts.filter(account => account.type === 'OtherCurrentAsset');
    },
    values() {
      return this.$store.getters.company.permissions.includes('values') && this.$store.getters.company.settings.useAccounts;
    }
  },
  watch: {
    item: {
      handler() {this.dirty = true},
      immediate: true,
      deep: true
    }
  },
  async created() {
    this.loading = true;
    this.$lazyListLoad(this.taxes, 'taxes2', 'id,name,salesRate,purchaseRate', 100);
    const results = await this.$apiCall(`query InventoryItemsEdit($currency:String!,$edit:Boolean!,$id:ID!) {
                                            item(id:$id) @include(if:$edit) {
                                              id,type,name,sku,active,lotTracking,location,
                                              assembled,description,bom {id,quantity,optional},
                                              trackUnits,precision,reorderPoint,allowNegativeInventory,
                                              category{id}
                                              usageCost,
                                              inventoryAccountRef,incomeAccountRef,cogAccountRef,
                                              buyingUnits,buyingPrecision,convertToTracking,weight,upc,reorderQuantity,reorderDays,reorderBusinessDays,
                                              buyingDescription,
                                              preferredVendor{id},
                                              sold,sellingDescription,defaultPrice,defaultTax{id},
                                              vendors{vendorId,tax{id},sku,cost},
                                              customers{category,customerId,tax{id},sku,price},commissionExempt,
                                              pictureURL
                                              productionPlanningInfo {useAverageDuration,specifiedDuration,equipmentTypes{id,quantity},roleId}
                                            }
                                            accounts(currency:$currency) {
                                              ref,name,type,typeName
                                            }
                                            companyAccounts {
                                              itemInventory,itemCOG,itemIncome,
                                              nonInventoryUsage,nonInventoryExpense,nonInventoryIncome,
                                              serviceUsage,serviceExpense,serviceIncome
                                            }
                                          }`,
                                        {currency:this.$store.getters.company.settings.homeCurrency,
                                         edit:this.itemId!==undefined,id:this.itemId?this.itemId:''});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }

    this.accounts = results.data.accounts;
    this.companyAccounts = results.data.companyAccounts;

    if (this.itemId) {
      this.item = {...results.data.item,
                   preferredVendorId:results.data.item.preferredVendor?.id,preferredVendor:undefined,
                   defaultTaxId:results.data.item.defaultTax?.id,defaultTax:undefined,
                   categoryId:results.data.item.category?.id,category:undefined,
                   vendors:results.data.item.vendors.map(v => ({...v,taxId:v.tax.id,tax:undefined})),
                   customers:results.data.item.customers.map(c => ({...c,taxId:c.tax.id,tax:undefined}))
                  };
      let key = Date.now();
      this.item.bom.forEach(b => b.key = key++);
    }
    else {
      if (this.connectedToAccounting) {
        if (this.inventoryAccounts.length === 0)
          this.disallowItem = 'A tracked inventory item requires an Other Current Asset account to be defined';
      }

      this.item = {type:this.disallowItem?'NonTracking':'Item',name:'',sku:'',active:true,lotTracking:true,location:'',
                   trackUnits:'items',precision:0,reorderPoint:-1,allowNegativeInventory:false,assembled:false,description:'',bom:[],
                   categoryId:null,usageCost:0,
                   inventoryAccountRef:this.disallowItem?this.companyAccounts.nonInventoryUsage:this.companyAccounts.itemInventory,
                   incomeAccountRef:this.disallowItem?this.companyAccounts.nonInventoryIncome:this.companyAccounts.itemIncome,
                   cogAccountRef:this.disallowItem?this.companyAccounts.nonInventoryExpense:this.companyAccounts.itemCOG,
                   buyingUnits:'items',buyingPrecision:0,convertToTracking:1,weight:0,upc:'',
                   reorderQuantity:0,reorderDays:0,reorderBusinessDays:false,
                   buyingDescription:'',vendors:[],preferredVendorId:null,
                   sold:false,sellingDescription:'',defaultPrice:0,defaultTaxId:null,customers:[],commissionExempt:false,
                   productionPlanningInfo:{useAverageDuration:true,specifiedDuration:0,equipmentTypes:[],roleId:''},
                   initial:[],initialAsof:format(new Date(), 'yyyy-MM-dd HH:mm:ss')};
    }
    this.$nextTick(() => this.dirty = false);
    this.loading = false;
  },
  methods: {
    cancel() {
      this.$router.back();
    },
    async save() {
      let valid = true;
      if (!this.$refs.info.valid) {
        this.tab = 0;
        valid = false;
      }
      else if (!this.itemId && this.item.type === 'Item' && this.$refs.init && !this.$refs.init.valid) {
        this.tab = 1;
        valid = false;
      }
      let tabOffset = !this.itemId && this.item.type === 'Item' ? 1 : 0;

      if (valid && this.item.assembled && !this.$refs.assembled.valid) {
        this.tab = 1 + tabOffset;
        valid = false;
      }
      else if (this.item.assembled)
        tabOffset++;

      if (valid && this.$refs.buying && !this.$refs.buying.valid) {
        this.tab = 1 + tabOffset;
        valid = false;
      }
      if (this.$refs.selling && !this.$refs.selling.valid) {
        this.tab = 2 + tabOffset;
        valid = false;
      }

      if (!valid) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }

      this.saving = true;
      // eslint-disable-next-line no-unused-vars
      const {pictureURL,...input} = this.item;
      if (this.item.id)
        delete input.type;
      else
        input.initial = input.initial.map(init => {     // Remove id field from initial list
          const {id,...line} = init;
          return line;
        });
      input.bom = input.bom.map(b => {
        const {key, ...bCore} = b;
        return bCore;
      });
      const results = await this.$apiCall(`mutation InventoryItemsEdit($input: ItemInput!) {
                                             itemCreate(input:$input) {id,name,type,trackUnits,precision,category{id},active,sold,upc}
                                           }`,
                                          {input})
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$store.commit(this.item.id ? 'itemUpdate' : 'itemAdd', results.data.itemCreate);
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.item.id?'Updated':'Created'} item ${this.item.name}`});
      }
    }
  }
}
</script>